/**
 * Kidney scoring — web copy (4.37 S1).
 * LINE-IDENTICAL with functions/src/utils/scoring.ts (the server, which
 * persists scores) and services/kidney/scoring.ts below the IMPORTS-END
 * marker; __tests__/scoring.parity.test.ts asserts it. Edit all three together.
 */

import type { KidneyProfile } from '@/types'
import { resolveNutrientTargets, isMinOnly } from './targetResolution'

// IMPORTS-END (everything below this line is asserted identical across copies)

/*
 * ONE frozen, versioned kidney scorer shared by the server (which persists
 * scores) and the clients (which re-score after a food edit, 4.37 S1).
 *
 * Numeric basis (frozen — `rawPerServingFoods-v1`):
 *   - raw per-serving food values, summed in `foods` order with `value || 0`;
 *   - the model's own meal totals are never used (they are overwritten by that sum);
 *   - NO nutrient rounding before scoring;
 *   - protein limits are rounded (1 decimal) during limit resolution;
 *   - the score (integer) and the percentages (1 decimal) are rounded after.
 *
 * Formula (v6 — capped protein curve + worst + secondary penalty):
 *   For each of (sodium, potassium, phosphorus): pct = nutrient / dailyLimit * 100.
 *   Protein is a soft curve (a target to hit, not a ceiling to fear):
 *     0 until 60% of daily (75% for dialysis), 1.75 slope to 100%, 0.5 beyond,
 *     capped at 90 — protein alone can flag a meal HIGH but never claim 100,
 *     which is reserved for Na/K/P blowouts and toxic foods.
 *   worst       = largest of [naPct, kPct, pPct, proteinScored]
 *   secondWorst = 2nd-largest
 *   penalty     = 0.2 * max(0, secondWorst - 30)
 *   V4 adds a half-weight tertiary term 0.1 * max(0, third - 30).
 *   score       = clamp(round(worst + penalty), 0, 100)
 *   0-30 safe · 31-60 moderate · 61-100 high.
 */

// ── Formula ─────────────────────────────────────────────────────────────────

export interface KidneyNutrients {
  sodiumMg: number;
  potassiumMg: number;
  phosphorusMg: number;
  proteinG?: number;
}

/**
 * User's daily nutrient limits. Sodium/potassium/phosphorus always required;
 * protein is optional (omitted for users without a protein restriction).
 */
export interface DailyLimits {
  sodiumMg: number;
  potassiumMg: number;
  phosphorusMg: number;
  /** Daily protein ceiling in grams. Omit to skip protein from scoring. */
  proteinG?: number;
  /**
   * Dialysis: protein is a TARGET (1.0-1.2 g/kg), not a ceiling. When true the
   * protein soft curve starts at 75% of daily instead of 60%.
   */
  proteinIsTarget?: boolean;
}

export interface ScoreExclusions {
  sodium?: boolean;
  potassium?: boolean;
  phosphorus?: boolean;
  protein?: boolean;
}

export interface ScoreOptions {
  /** V4: count the 3rd-worst nutrient too (half the weight of the 2nd). */
  tertiaryPenalty?: boolean;
  /**
   * V4: phosphate additives are ~90-100% absorbed vs ~40-60% for natural P.
   * Weights phosphorus by this factor FOR SCORING ONLY — displayed mg and
   * percentages never change.
   */
  phosphorusWeight?: number;
  /**
   * Potassium is not restricted for this user (no kidney diagnosis, or stone
   * former): excluded from the score/driver. Displayed % and level stay real.
   */
  potassiumUnrestricted?: boolean;
  /**
   * 4.32 min-only targets: nutrients whose ONLY bound is a user-set floor have
   * no ceiling to score against — excluded from score/driver.
   */
  excludeFromScore?: ScoreExclusions;
}

/** @deprecated alias kept for callers that still import the old name. */
export type ScoreThresholds = DailyLimits;

export type RiskLevel = 'safe' | 'moderate' | 'high';
export type ScoreDriver = 'sodium' | 'potassium' | 'phosphorus' | 'protein';
export type NutrientLevel = 'low' | 'moderate' | 'high';

export interface ScoreBreakdown {
  score: number;
  riskLevel: RiskLevel;
  driver: ScoreDriver;
  /** Percent of DAILY limit consumed by each nutrient, rounded to 1 decimal */
  percentages: {
    sodium: number;
    potassium: number;
    phosphorus: number;
    protein: number | null;
  };
  /** The daily limits used to compute the percentages, echoed for auditability */
  dailyLimits: {
    sodiumMg: number;
    potassiumMg: number;
    phosphorusMg: number;
    proteinG: number | null;
  };
  /** Per-nutrient coarse level (low/moderate/high) for legacy UI fields */
  levels: {
    potassium: NutrientLevel;
    phosphorus: NutrientLevel;
    sodium: NutrientLevel;
  };
}

export function scoreFromNutrients(
  nutrients: KidneyNutrients,
  dailyLimits: DailyLimits,
  options?: ScoreOptions,
): ScoreBreakdown {
  const excl = options?.excludeFromScore;
  const naPct = pct(nutrients.sodiumMg, dailyLimits.sodiumMg);
  const naScored = excl?.sodium ? 0 : naPct;
  const kPct = pct(nutrients.potassiumMg, dailyLimits.potassiumMg);
  // Unrestricted / min-only potassium contributes nothing to the score; kPct
  // still feeds the displayed percentage/level below.
  const kExcluded = options?.potassiumUnrestricted || excl?.potassium;
  const kScored = kExcluded ? 0 : kPct;
  // Additive-P weighting applies to the SCORED percentage only.
  const pPctDisplay = pct(nutrients.phosphorusMg, dailyLimits.phosphorusMg);
  const pPct = excl?.phosphorus ? 0 : pPctDisplay * (options?.phosphorusWeight ?? 1);

  // Protein participates when the user has a protein limit and we have a value.
  const proteinPctRaw =
    dailyLimits.proteinG != null && nutrients.proteinG != null
      ? pct(nutrients.proteinG, dailyLimits.proteinG)
      : null;
  const proteinCurveStart = dailyLimits.proteinIsTarget ? 75 : 60;
  const proteinPctScored =
    proteinPctRaw != null && !excl?.protein ? proteinScoredFromPct(proteinPctRaw, proteinCurveStart) : 0;

  const sorted = [naScored, kScored, pPct, proteinPctScored].sort((a, b) => b - a);
  const worst = sorted[0];
  const second = sorted[1] ?? 0;
  const third = sorted[2] ?? 0;

  // Driver tie-break: K > P > Na > Protein (hyperkalemia most acute in CKD)
  let driver: ScoreDriver;
  if (kScored === worst && !kExcluded) driver = 'potassium';
  else if (pPct === worst && !excl?.phosphorus) driver = 'phosphorus';
  else if (naScored === worst && !excl?.sodium) driver = 'sodium';
  else driver = 'protein';

  const secondaryPenalty = 0.2 * Math.max(0, second - 30);
  const tertiaryPenalty = options?.tertiaryPenalty ? 0.1 * Math.max(0, third - 30) : 0;
  const score = Math.round(Math.min(100, Math.max(0, worst + secondaryPenalty + tertiaryPenalty)));
  const riskLevel: RiskLevel = riskFromScore(score);

  return {
    score,
    riskLevel,
    driver,
    percentages: {
      sodium: round1(naPct),
      potassium: round1(kPct),
      // Displayed % is always the unweighted label value.
      phosphorus: round1(pPctDisplay),
      protein: proteinPctRaw != null ? round1(proteinPctRaw) : null,
    },
    dailyLimits: {
      sodiumMg: dailyLimits.sodiumMg,
      potassiumMg: dailyLimits.potassiumMg,
      phosphorusMg: dailyLimits.phosphorusMg,
      proteinG: dailyLimits.proteinG ?? null,
    },
    levels: {
      potassium: pctToLevel(kPct),
      phosphorus: pctToLevel(pPctDisplay),
      sodium: pctToLevel(naPct),
    },
  };
}

/** 0-30 safe · 31-60 moderate · 61-100 high. */
export function riskFromScore(score: number): RiskLevel {
  return score <= 30 ? 'safe' : score <= 60 ? 'moderate' : 'high';
}

function proteinScoredFromPct(proteinPct: number, start: number): number {
  if (proteinPct <= start) return 0;
  const belowDaily = Math.min(proteinPct, 100) - start;
  const aboveDaily = Math.max(0, proteinPct - 100);
  return Math.min(90, belowDaily * 1.75 + aboveDaily * 0.5);
}

function pct(value: number, limit: number): number {
  if (!isFinite(value) || value <= 0) return 0;
  if (!isFinite(limit) || limit <= 0) return 0;
  return (value / limit) * 100;
}

function round1(n: number): number {
  return Math.round(n * 10) / 10;
}

/** Legacy coarse level: under 30% of the day low, 30-60% moderate, >60% high. */
function pctToLevel(p: number): NutrientLevel {
  if (p < 30) return 'low';
  if (p < 60) return 'moderate';
  return 'high';
}

// ── Limits resolver (port of the server's getDailyLimits) ───────────────────

const STAGE_3_DEFAULTS = {
  sodiumMg: 2000,
  potassiumMg: 2500,
  phosphorusMg: 800,
  proteinPerKg: 0.8,
};

// General health (no kidney diagnosis): population DRI.
const GENERAL_HEALTH_DRI = {
  sodiumMg: 2300,
  potassiumMg: 4000,
  phosphorusMg: 1000,
  proteinPerKg: 0.8,
};

// Stone-only profiles without calculated limits: K/P are not restricted.
const STONE_FORMER_DEFAULTS = {
  sodiumMg: 2300,
  potassiumMg: 4000,
  phosphorusMg: 1000,
  proteinPerKg: 0.8,
};

const DEFAULT_WEIGHT_KG = 70;

// 4.46 F7a: the two calculated maps the resolver rebuilds at read time — the Stage 5
// (not on dialysis) row and the dialysis row of calculateNutrientLimits
// (services/kidney/nutrientLimits.ts, webapp/src/kidney/limits.ts; parity:
// __tests__/startingSoon446.test.ts). They live here because this is the one module
// the server, the phone and the web share line-identically.
const STAGE_5_CALCULATED: CalculatedLimitsMap = {
  sodium: 1500,
  potassium: 2000,
  phosphorus: 700,
  proteinPerKg: 0.6,
  proteinRange: '0.6g/kg',
};
const DIALYSIS_CALCULATED: CalculatedLimitsMap = {
  sodium: 2000,
  potassium: 2500,
  phosphorus: 1000,
  proteinPerKg: 1.1,
  proteinRange: '1.0-1.2g/kg',
};
// Dialysis types that mean "on dialysis now" (`not_started` is the fourth value).
const ACTIVE_DIALYSIS_TYPES = new Set(['hemodialysis', 'peritoneal', 'home_hemo']);

/** A `kidneyProfile.calculatedLimits` map, as the calculators store it. */
export interface CalculatedLimitsMap {
  sodium: number;
  potassium: number;
  phosphorus: number;
  proteinPerKg: number;
  proteinRange?: string;
}

export type LimitSource = 'calculatedLimits' | 'generalHealth' | 'stoneFormer' | 'stage3Default';

export type CoreScoringKey = 'sodium' | 'potassium' | 'phosphorus' | 'protein';

export interface DailyLimitsResult extends DailyLimits {
  source: LimitSource;
  /**
   * 4.32 min-only targets: nutrients whose ONLY bound is a user-set floor. The
   * mg/g field above carries the FLOOR (so displayed % match the diary card)
   * and the nutrient is excluded from ceiling scoring.
   */
  minOnlyNutrients?: CoreScoringKey[];
  /** Resolved daily floors (min-only AND ranges), for prompt construction. */
  minTargets?: Partial<Record<CoreScoringKey, number>>;
  /**
   * 4.35: nutrients whose CEILING was set explicitly (legacy `daily*Limit` or
   * a canonical `nutrientTargets.{key}.max`). A prescribed ceiling always wins
   * over a condition-based relaxation — see isPotassiumUnrestricted().
   */
  explicitMaxNutrients?: CoreScoringKey[];
}

/** The profile shape the resolver reads (every platform's UserProfile fits). */
export interface ScoringProfile {
  kidneyProfile?: KidneyProfile | null;
}

/** Loose view of the fields the resolver branches on (platform types differ). */
interface ScoringKidneyFields {
  condition?: string | null;
  onDialysis?: boolean | null;
  /** 4.46 F7a: `not_started` (Starting soon) changes the calculated map and protein semantics. */
  dialysisType?: unknown;
  comorbidities?: unknown;
  weight?: unknown;
  calculatedLimits?: {
    sodium?: unknown;
    potassium?: unknown;
    phosphorus?: unknown;
    proteinPerKg?: unknown;
  } | null;
  dailySodiumLimit?: unknown;
  dailyPotassiumLimit?: unknown;
  dailyPhosphorusLimit?: unknown;
  dailyProteinLimit?: unknown;
  nutrientTargets?: unknown;
}

// Conditions that mean "no kidney diagnosis" — every value the app and funnel write.
const GENERAL_HEALTH_CONDITIONS = new Set(['none', 'not_sure_prevention', 'at_risk', 'no_diagnosis', 'not_sure']);
// Conditions that themselves mean "on dialysis".
const DIALYSIS_CONDITIONS = new Set(['dialysis', 'on_dialysis', 'ckd_stage_5_dialysis']);

function fieldsOf(kidneyProfile: KidneyProfile | null | undefined): ScoringKidneyFields | undefined {
  return (kidneyProfile ?? undefined) as ScoringKidneyFields | undefined;
}

/** STONE-ONLY: the condition itself is kidney stones (not CKD). */
function isStoneOnlyProfile(k: ScoringKidneyFields | undefined): boolean {
  return k?.condition === 'kidney_stones';
}

function isGeneralHealthProfile(k: ScoringKidneyFields | undefined): boolean {
  const condition = k?.condition;
  return !!condition && GENERAL_HEALTH_CONDITIONS.has(condition);
}

/**
 * The `onDialysis` flag can go stale (old Settings saves changed the condition
 * without clearing it). Trust it only when the condition doesn't contradict it
 * (stage 5, or no condition set at all).
 */
function isDialysisProfile(k: ScoringKidneyFields | undefined): boolean {
  if (!k) return false;
  const condition = k.condition || '';
  if (DIALYSIS_CONDITIONS.has(condition)) return true;
  return k.onDialysis === true && (condition === '' || condition === 'ckd_stage_5');
}

/**
 * 4.47 A1 — protein is a GOAL to reach (a floor), not a ceiling: a dialysis profile
 * (the stale-flag rule above) whose type is not Starting soon (4.46 F7a: `not_started`
 * keeps protein a ceiling until dialysis starts). ONE rule for the scorer
 * (`proteinIsTarget`), the daily targets (`resolveDailyTargets`) and both doctor reports.
 */
export function proteinIsGoalProfile(kidneyProfile: KidneyProfile | null | undefined): boolean {
  return proteinIsGoalOf(fieldsOf(kidneyProfile));
}

function proteinIsGoalOf(k: ScoringKidneyFields | undefined): boolean {
  return isDialysisProfile(k) && k?.dialysisType !== 'not_started';
}

/**
 * 4.46 F7a — the calculated-limits map a profile is judged by. ONE implementation for
 * the server, the phone, the web and the migration (scripts/f7a-starting-soon-limits.cjs):
 *  1. a dialysis status + `dialysisType: 'not_started'` (Starting soon) → the Stage 5
 *     (not on dialysis) map with the comorbidity rule, whatever is stored (an old client
 *     stores dialysis numbers; a profile with no stored map gets it too);
 *  2. a dialysis status + an active type whose stored map is pre-dialysis
 *     (`proteinPerKg` a finite number in (0, 1.0)) → the dialysis map with the
 *     comorbidity rule (an old client changed only the type of a migrated profile);
 *  3. otherwise the stored map when its sodium is a number, else undefined (the caller
 *     keeps its own fallback).
 * Keyed on the condition only: untyped and stale-flag profiles keep their stored numbers.
 * Canonical targets and legacy overrides still win over this map (contract point 7).
 */
export function effectiveCalculatedLimits(
  kidneyProfile: KidneyProfile | null | undefined,
): CalculatedLimitsMap | undefined {
  return effectiveMapOf(fieldsOf(kidneyProfile));
}

function effectiveMapOf(k: ScoringKidneyFields | undefined): CalculatedLimitsMap | undefined {
  if (!k) return undefined;
  const stored = k.calculatedLimits;
  if (DIALYSIS_CONDITIONS.has(k.condition || '')) {
    if (k.dialysisType === 'not_started') return withComorbidities(STAGE_5_CALCULATED, k.comorbidities);
    const perKg = stored?.proteinPerKg;
    if (
      typeof k.dialysisType === 'string' &&
      ACTIVE_DIALYSIS_TYPES.has(k.dialysisType) &&
      typeof perKg === 'number' &&
      Number.isFinite(perKg) &&
      perKg > 0 &&
      perKg < 1.0
    ) {
      return withComorbidities(DIALYSIS_CALCULATED, k.comorbidities);
    }
  }
  return stored && typeof stored.sodium === 'number' ? (stored as CalculatedLimitsMap) : undefined;
}

/** calculateNutrientLimits' comorbidity rule: −300 mg sodium each for high blood pressure and heart disease, floor 1000. */
function withComorbidities(base: CalculatedLimitsMap, comorbidities: unknown): CalculatedLimitsMap {
  const list: unknown[] = Array.isArray(comorbidities) ? comorbidities : [];
  let sodium = base.sodium;
  if (list.includes('high_blood_pressure')) sodium -= 300;
  if (list.includes('heart_disease')) sodium -= 300;
  return { ...base, sodium: Math.max(1000, sodium) };
}

/**
 * Condition-based potassium relaxation (general-health and stone-only users),
 * EXCEPT when the user has an explicit potassium ceiling: a prescribed maximum
 * must be scored. A minimum-only potassium goal keeps the relaxation.
 */
export function isPotassiumUnrestricted(
  kidneyProfile: KidneyProfile | null | undefined,
  limits: DailyLimitsResult,
): boolean {
  const k = fieldsOf(kidneyProfile);
  if (!(isGeneralHealthProfile(k) || isStoneOnlyProfile(k))) return false;
  return !limits.explicitMaxNutrients?.includes('potassium');
}

/** ScoreOptions.excludeFromScore payload for the user's min-only nutrients. */
export function minOnlyScoreExclusions(limits: DailyLimitsResult): ScoreExclusions | undefined {
  if (!limits.minOnlyNutrients?.length) return undefined;
  const out: ScoreExclusions = {};
  for (const k of limits.minOnlyNutrients) out[k] = true;
  return out;
}

/**
 * The user's DAILY nutrient limits for scoring (mg/g per day).
 * Priority: calculatedLimits → stone-only defaults → general-health DRI →
 * conservative Stage 3; then legacy doctor overrides; then canonical
 * nutrientTargets (which win when present).
 */
export function getDailyLimits(profile: ScoringProfile | null | undefined): DailyLimitsResult {
  const kidneyProfile = profile?.kidneyProfile ?? undefined;
  const base = getBaseDailyLimits(fieldsOf(kidneyProfile));
  const withDoctor = applyDoctorOverrides(base, fieldsOf(kidneyProfile));
  return applyNutrientTargets(withDoctor, kidneyProfile);
}

/** @deprecated alias — returns the same DAILY limits. */
export const getMealThresholds = getDailyLimits;

function applyNutrientTargets(
  base: DailyLimitsResult,
  kidneyProfile: KidneyProfile | undefined,
): DailyLimitsResult {
  if (!kidneyProfile?.nutrientTargets) return base;
  const resolved = resolveNutrientTargets(kidneyProfile, {
    sodium: base.sodiumMg,
    potassium: base.potassiumMg,
    phosphorus: base.phosphorusMg,
    protein: base.proteinG ?? 0,
    fluid: 0,
  });
  const out: DailyLimitsResult = { ...base };
  const minOnly: CoreScoringKey[] = [];
  const minTargets: Partial<Record<CoreScoringKey, number>> = {};
  const explicitMax = new Set<CoreScoringKey>();
  const keys: CoreScoringKey[] = ['sodium', 'potassium', 'phosphorus', 'protein'];
  for (const key of keys) {
    const target = resolved[key];
    if (target.min !== undefined) minTargets[key] = target.min;
    if (isMinOnly(target)) {
      minOnly.push(key);
      // Field carries the floor so displayed %s match the diary card.
      if (key === 'protein') out.proteinG = target.min;
      else if (key === 'sodium') out.sodiumMg = target.min!;
      else if (key === 'potassium') out.potassiumMg = target.min!;
      else out.phosphorusMg = target.min!;
    } else if (target.max !== undefined) {
      if (target.source === 'custom') explicitMax.add(key);
      if (key === 'protein') out.proteinG = target.max;
      else if (key === 'sodium') out.sodiumMg = target.max;
      else if (key === 'potassium') out.potassiumMg = target.max;
      else out.phosphorusMg = target.max;
    }
  }
  if (minOnly.length) out.minOnlyNutrients = minOnly;
  if (Object.keys(minTargets).length) out.minTargets = minTargets;
  // The canonical map is authoritative when present: replace the legacy pass.
  if (explicitMax.size) out.explicitMaxNutrients = [...explicitMax];
  else delete out.explicitMaxNutrients;
  return out;
}

function getBaseDailyLimits(k: ScoringKidneyFields | undefined): DailyLimitsResult {
  // 4.46 F7a: Starting soon is not on dialysis yet — protein is a ceiling until it starts.
  // 4.47 A1: the shared predicate (the golden pins it).
  const proteinIsTarget = proteinIsGoalOf(k);

  // Path 1 — calculated limits present (the effective map; F7a may rebuild it)
  const limits = effectiveMapOf(k);
  if (limits) {
    const weightKg = safeWeight(k?.weight);
    const proteinDaily =
      typeof limits.proteinPerKg === 'number' && limits.proteinPerKg > 0
        ? limits.proteinPerKg * weightKg
        : undefined;
    return {
      sodiumMg: limits.sodium,
      potassiumMg: limits.potassium as number,
      phosphorusMg: limits.phosphorus as number,
      proteinG: proteinDaily != null ? round1(proteinDaily) : undefined,
      proteinIsTarget,
      source: 'calculatedLimits',
    };
  }

  // Path 2a — stone former without calculated limits: K/P are not restricted
  if (isStoneOnlyProfile(k)) {
    const weightKg = safeWeight(k?.weight);
    return {
      sodiumMg: STONE_FORMER_DEFAULTS.sodiumMg,
      potassiumMg: STONE_FORMER_DEFAULTS.potassiumMg,
      phosphorusMg: STONE_FORMER_DEFAULTS.phosphorusMg,
      proteinG: round1(STONE_FORMER_DEFAULTS.proteinPerKg * weightKg),
      proteinIsTarget,
      source: 'stoneFormer',
    };
  }

  // Path 2 — general-health user
  if (isGeneralHealthProfile(k)) {
    const weightKg = safeWeight(k?.weight);
    return {
      sodiumMg: GENERAL_HEALTH_DRI.sodiumMg,
      potassiumMg: GENERAL_HEALTH_DRI.potassiumMg,
      phosphorusMg: GENERAL_HEALTH_DRI.phosphorusMg,
      proteinG: round1(GENERAL_HEALTH_DRI.proteinPerKg * weightKg),
      proteinIsTarget,
      source: 'generalHealth',
    };
  }

  // Path 3 — no profile; conservative Stage 3
  const weightKg = safeWeight(k?.weight);
  return {
    sodiumMg: STAGE_3_DEFAULTS.sodiumMg,
    potassiumMg: STAGE_3_DEFAULTS.potassiumMg,
    phosphorusMg: STAGE_3_DEFAULTS.phosphorusMg,
    proteinG: round1(STAGE_3_DEFAULTS.proteinPerKg * weightKg),
    proteinIsTarget,
    source: 'stage3Default',
  };
}

/**
 * Doctor-set per-nutrient limits (legacy `daily*Limit`) win over calculated
 * limits. Protein override is g/day directly (no weight multiplication).
 */
function applyDoctorOverrides(base: DailyLimitsResult, k: ScoringKidneyFields | undefined): DailyLimitsResult {
  if (!k) return base;
  const sodium = doctorLimit(k.dailySodiumLimit);
  const potassium = doctorLimit(k.dailyPotassiumLimit);
  const phosphorus = doctorLimit(k.dailyPhosphorusLimit);
  const protein = doctorLimit(k.dailyProteinLimit);
  if (sodium == null && potassium == null && phosphorus == null && protein == null) {
    return base;
  }
  const explicitMaxNutrients: CoreScoringKey[] = [];
  if (sodium != null) explicitMaxNutrients.push('sodium');
  if (potassium != null) explicitMaxNutrients.push('potassium');
  if (phosphorus != null) explicitMaxNutrients.push('phosphorus');
  if (protein != null) explicitMaxNutrients.push('protein');
  return {
    ...base,
    explicitMaxNutrients,
    sodiumMg: sodium ?? base.sodiumMg,
    potassiumMg: potassium ?? base.potassiumMg,
    phosphorusMg: phosphorus ?? base.phosphorusMg,
    proteinG: protein ?? base.proteinG,
  };
}

function doctorLimit(n: unknown): number | undefined {
  return typeof n === 'number' && isFinite(n) && n > 0 ? n : undefined;
}

function safeWeight(weight: unknown): number {
  if (typeof weight !== 'number' || !Number.isFinite(weight) || weight <= 20 || weight > 300) {
    return DEFAULT_WEIGHT_KG;
  }
  return weight;
}

// ── Resolved scoring inputs (what a score was computed against) ─────────────

/**
 * Additive phosphorus is ~90-100% absorbed vs ~40-60% for natural P, so under
 * V4 a meal with flagged phosphate additives has its phosphorus weighted up
 * FOR SCORING ONLY. 1.25 is deliberately conservative (the flag is meal-level).
 */
export const ADDITIVE_PHOSPHORUS_WEIGHT = 1.25;

/**
 * Everything the scorer needs, fully resolved — no profile access after this.
 * The server resolves it from the profile; a client replays it from a stored
 * scoreContext (4.37 S1b) so an edit is scored against the SAME inputs.
 */
export interface ResolvedScoring {
  limits: DailyLimits;
  limitsSource: LimitSource;
  potassiumUnrestricted: boolean;
  exclude: Required<ScoreExclusions>;
  /** The resolved V4 headline decision (non-V4: no tertiary term, weight 1). */
  headline: { tertiaryPenalty: boolean; phosphorusWeight: number };
  /** The meal-level additive multiplier was applied (V4 + flagged additives). */
  additiveWeighting: boolean;
}

/** Resolve the scoring inputs for a profile + the analyzer's V4 decision. */
export function resolveScoring(
  profile: ScoringProfile | null | undefined,
  opts?: { v4?: boolean; hasAdditives?: boolean },
): ResolvedScoring {
  const dailyLimits = getDailyLimits(profile);
  const potassiumUnrestricted = isPotassiumUnrestricted(profile?.kidneyProfile, dailyLimits);
  const minOnly = minOnlyScoreExclusions(dailyLimits);
  const v4 = opts?.v4 === true;
  return {
    limits: {
      sodiumMg: dailyLimits.sodiumMg,
      potassiumMg: dailyLimits.potassiumMg,
      phosphorusMg: dailyLimits.phosphorusMg,
      proteinG: dailyLimits.proteinG,
      proteinIsTarget: dailyLimits.proteinIsTarget === true,
    },
    limitsSource: dailyLimits.source,
    potassiumUnrestricted,
    exclude: {
      sodium: minOnly?.sodium === true,
      potassium: minOnly?.potassium === true,
      phosphorus: minOnly?.phosphorus === true,
      protein: minOnly?.protein === true,
    },
    headline: { tertiaryPenalty: v4, phosphorusWeight: v4 ? ADDITIVE_PHOSPHORUS_WEIGHT : 1 },
    additiveWeighting: v4 && opts?.hasAdditives === true,
  };
}

/** Options for the meal headline score (V4 terms + exclusions). */
export function mealScoreOptions(r: ResolvedScoring): ScoreOptions {
  return {
    tertiaryPenalty: r.headline.tertiaryPenalty,
    phosphorusWeight: r.additiveWeighting ? r.headline.phosphorusWeight : 1,
    potassiumUnrestricted: r.potassiumUnrestricted,
    excludeFromScore: r.exclude,
  };
}

/** Options for a single food's badge: exclusions only (never V4 terms). */
export function foodScoreOptions(r: ResolvedScoring): ScoreOptions {
  return { potassiumUnrestricted: r.potassiumUnrestricted, excludeFromScore: r.exclude };
}

/** Anything carrying per-serving core nutrients (identified food, stored food). */
export interface ScorableFood {
  name?: string | null;
  sodiumMg?: number | null;
  potassiumMg?: number | null;
  phosphorusMg?: number | null;
  proteinG?: number | null;
}

/** Meal core totals: raw per-serving values summed in `foods` order, `value || 0`. */
export function sumCoreNutrients(foods: readonly ScorableFood[]): Required<KidneyNutrients> {
  return {
    sodiumMg: foods.reduce((sum, f) => sum + (f.sodiumMg || 0), 0),
    potassiumMg: foods.reduce((sum, f) => sum + (f.potassiumMg || 0), 0),
    phosphorusMg: foods.reduce((sum, f) => sum + (f.phosphorusMg || 0), 0),
    proteinG: foods.reduce((sum, f) => sum + (f.proteinG || 0), 0),
  };
}

/** The meal headline breakdown from core totals. */
export function scoreMealTotals(totals: KidneyNutrients, r: ResolvedScoring): ScoreBreakdown {
  return scoreFromNutrients(totals, r.limits, mealScoreOptions(r));
}

export type FoodSafety = 'safe' | 'moderate' | 'limit';

/** Per-food badge: same engine + limits as the headline, so they never contradict. */
export function scoreFood(
  f: ScorableFood,
  r: ResolvedScoring,
): { kidneySafety: FoodSafety; riskLevel: RiskLevel; primaryConcern: ScoreDriver | null } {
  const fb = scoreFromNutrients(
    {
      sodiumMg: f.sodiumMg || 0,
      potassiumMg: f.potassiumMg || 0,
      phosphorusMg: f.phosphorusMg || 0,
      proteinG: f.proteinG ?? undefined,
    },
    r.limits,
    foodScoreOptions(r),
  );
  const kidneySafety: FoodSafety = riskToSafety(fb.riskLevel);
  return { kidneySafety, riskLevel: fb.riskLevel, primaryConcern: kidneySafety === 'safe' ? null : fb.driver };
}

/** RiskLevel → stored SafetyLevel ('high' is stored as 'limit'). */
export function riskToSafety(risk: RiskLevel): FoodSafety {
  return risk === 'high' ? 'limit' : risk;
}

/** Recipe per-serving score (recipes have no food array; no V4 terms). */
export function scoreServing(nutrients: ScorableFood, r: ResolvedScoring): ScoreBreakdown {
  return scoreFromNutrients(
    {
      sodiumMg: nutrients.sodiumMg || 0,
      potassiumMg: nutrients.potassiumMg || 0,
      phosphorusMg: nutrients.phosphorusMg || 0,
      proteinG: nutrients.proteinG ?? undefined,
    },
    r.limits,
    foodScoreOptions(r),
  );
}

// ── CKD-toxic foods ─────────────────────────────────────────────────────────

/**
 * CKD-TOXIC foods (caramboxin neurotoxin) — dangerous regardless of nutrient
 * profile, so any match forces the score to 100 (absolute ban). Matched
 * against food names in the user's language, hence the multilingual patterns:
 * star fruit (carambola) and bilimbi across the app's 13 locales.
 */
const TOXIC_FOOD_PATTERNS: RegExp[] = [
  /star\s*fruit/i, //           en
  /carambol/i, //               es/it/pt carambola, fr carambole, de Karambole, pl/tr karambola
  /bilimbi/i, //                Averrhoa bilimbi (all languages)
  /belimbing/i, //              Malay/Indonesian name the AI sometimes uses
  /sternfrucht/i, //            de
  /stervrucht/i, //             nl
  /yıldız\s*meyve/i, //         tr
  /楊桃|杨桃/, //                zh
  /スターフルーツ|ゴレンシ/, //     ja
  /스타\s*프루트|카람볼라/, //     ko
  /فاكهة النجمة|كارامبولا/, //   ar
];

export function isToxicFoodName(name: string | null | undefined): boolean {
  return !!name && TOXIC_FOOD_PATTERNS.some((p) => p.test(name));
}

/** Indices of foods whose names match a CKD-toxic pattern. */
export function findToxicFoodIndices(names: Array<string | null | undefined>): number[] {
  const indices: number[] = [];
  names.forEach((name, i) => {
    if (isToxicFoodName(name)) indices.push(i);
  });
  return indices;
}

// ── Score context (4.37 S1b): replaying a score after a food edit ──────────

/*
 * The server stamps `scoreContext` on meal / food / label / barcode analyses
 * (never the demos): the RESOLVED inputs the score was computed against, not
 * the profile. A client re-scores an edited meal from it, so an edit is judged
 * by exactly the limits, exclusions and V4 decision of the original analysis.
 *
 * Identity: the server assigns `key` ('f0', 'f1', …) to every identified food
 * and every mapper carries it; keys are never recreated from array positions.
 * Replay runs only when the version and basis are known, every food has a
 * unique non-empty key, the stored foods still add up to the stored parent
 * totals, and replaying the unedited foods reproduces the stored score. Any
 * failure is FAIL-CLOSED: the stored score is kept and marked stale (legacy).
 *
 * Additives stay meal-level: `additiveWeighting` is frozen as the original
 * analysis applied it and stays on through edits until a fresh analysis.
 * Toxic foods: the override stays while any food whose key is in `toxic`
 * remains (a renamed toxic food stays toxic); it lifts only when that exact
 * food is removed. A food whose name matches a toxic pattern is toxic too.
 */

export const SCORE_CONTEXT_VERSION = 1;
export const SCORE_BASIS = 'rawPerServingFoods-v1';

export interface ScoreContextToxic {
  foodKey: string;
  name: string;
}

export interface ScoreContext {
  v: number;
  basis: string;
  limits: { sodiumMg: number; potassiumMg: number; phosphorusMg: number; proteinG: number | null };
  limitsSource: LimitSource;
  proteinIsTarget: boolean;
  potassiumUnrestricted: boolean;
  exclude: Required<ScoreExclusions>;
  /** The resolved V4 decision, frozen. */
  headline: { tertiaryPenalty: boolean; phosphorusWeight: number };
  /** True when the original analysis applied the meal-level additive multiplier. */
  additiveWeighting: boolean;
  toxic: ScoreContextToxic[];
}

/** Server: the identity key of the i-th identified food. */
export function foodKeyAt(index: number): string {
  return `f${index}`;
}

/** A key for a food the user added on the client; never collides with `existing`. */
export function newFoodKey(existing: ReadonlyArray<string | null | undefined>): string {
  const used = new Set(existing.filter((k): k is string => typeof k === 'string'));
  let n = 0;
  while (used.has(`u${n}`)) n += 1;
  return `u${n}`;
}

export function buildScoreContext(r: ResolvedScoring, toxic: ScoreContextToxic[]): ScoreContext {
  return {
    v: SCORE_CONTEXT_VERSION,
    basis: SCORE_BASIS,
    limits: {
      sodiumMg: r.limits.sodiumMg,
      potassiumMg: r.limits.potassiumMg,
      phosphorusMg: r.limits.phosphorusMg,
      proteinG: r.limits.proteinG ?? null,
    },
    limitsSource: r.limitsSource,
    proteinIsTarget: r.limits.proteinIsTarget === true,
    potassiumUnrestricted: r.potassiumUnrestricted,
    exclude: { ...r.exclude },
    headline: { ...r.headline },
    additiveWeighting: r.additiveWeighting,
    toxic: toxic.map((x) => ({ foodKey: x.foodKey, name: x.name })),
  };
}

const LIMIT_SOURCES: readonly string[] = ['calculatedLimits', 'generalHealth', 'stoneFormer', 'stage3Default'];
const isNum = (n: unknown): n is number => typeof n === 'number' && Number.isFinite(n);
const isBool = (b: unknown): b is boolean => typeof b === 'boolean';

/** A stored context this client can replay, or null (unknown version / basis / damaged shape). */
export function parseScoreContext(raw: unknown): ScoreContext | null {
  if (!raw || typeof raw !== 'object') return null;
  const c = raw as Record<string, any>;
  if (c.v !== SCORE_CONTEXT_VERSION || c.basis !== SCORE_BASIS) return null;
  const l = c.limits;
  if (!l || !isNum(l.sodiumMg) || !isNum(l.potassiumMg) || !isNum(l.phosphorusMg)) return null;
  if (!(l.proteinG === null || isNum(l.proteinG))) return null;
  if (!LIMIT_SOURCES.includes(c.limitsSource)) return null;
  if (!isBool(c.proteinIsTarget) || !isBool(c.potassiumUnrestricted) || !isBool(c.additiveWeighting)) return null;
  const e = c.exclude;
  if (!e || !isBool(e.sodium) || !isBool(e.potassium) || !isBool(e.phosphorus) || !isBool(e.protein)) return null;
  const h = c.headline;
  if (!h || !isBool(h.tertiaryPenalty) || !isNum(h.phosphorusWeight)) return null;
  if (!Array.isArray(c.toxic)) return null;
  const toxic: ScoreContextToxic[] = [];
  for (const x of c.toxic) {
    if (!x || typeof x.foodKey !== 'string' || !x.foodKey) return null;
    toxic.push({ foodKey: x.foodKey, name: typeof x.name === 'string' ? x.name : '' });
  }
  return {
    v: SCORE_CONTEXT_VERSION,
    basis: SCORE_BASIS,
    limits: { sodiumMg: l.sodiumMg, potassiumMg: l.potassiumMg, phosphorusMg: l.phosphorusMg, proteinG: l.proteinG },
    limitsSource: c.limitsSource,
    proteinIsTarget: c.proteinIsTarget,
    potassiumUnrestricted: c.potassiumUnrestricted,
    exclude: { sodium: e.sodium, potassium: e.potassium, phosphorus: e.phosphorus, protein: e.protein },
    headline: { tertiaryPenalty: h.tertiaryPenalty, phosphorusWeight: h.phosphorusWeight },
    additiveWeighting: c.additiveWeighting,
    toxic,
  };
}

/** The resolved scoring inputs frozen in a context. */
export function resolvedFromScoreContext(ctx: ScoreContext): ResolvedScoring {
  return {
    limits: {
      sodiumMg: ctx.limits.sodiumMg,
      potassiumMg: ctx.limits.potassiumMg,
      phosphorusMg: ctx.limits.phosphorusMg,
      proteinG: ctx.limits.proteinG ?? undefined,
      proteinIsTarget: ctx.proteinIsTarget,
    },
    limitsSource: ctx.limitsSource,
    potassiumUnrestricted: ctx.potassiumUnrestricted,
    exclude: { ...ctx.exclude },
    headline: { ...ctx.headline },
    additiveWeighting: ctx.additiveWeighting,
  };
}

/** A food as the replay sees it: per-serving core nutrients + identity. */
export interface RescoreFood extends ScorableFood {
  key?: string | null;
}

/** Every food has a unique, non-empty key (and there is at least one food). */
export function hasUsableFoodKeys(foods: readonly RescoreFood[]): boolean {
  if (foods.length === 0) return false;
  const seen = new Set<string>();
  for (const f of foods) {
    if (typeof f.key !== 'string' || !f.key || seen.has(f.key)) return false;
    seen.add(f.key);
  }
  return true;
}

// Display rounding per core key: mg to whole numbers, g to one decimal.
const CORE_ROUNDING: Record<keyof Required<KidneyNutrients>, number> = {
  sodiumMg: 0.5,
  potassiumMg: 0.5,
  phosphorusMg: 0.5,
  proteinG: 0.05,
};

/**
 * Basis consistency: the per-serving foods must still add up to the stored
 * parent totals. `parent` holds the totals for `servings` servings (a diary
 * entry: its nutrients + servingsConsumed; a favorite or a fresh analysis: its
 * per-serving totals + 1). The tolerance is display rounding — one rounding
 * of the per-serving value (scaled by servings) plus one of the parent itself,
 * doubled for a favorite re-logged from a rounded entry. Catches an older
 * totals-only editor that changed the parent but kept a valid-looking context.
 */
export function basisMatches(
  foods: readonly ScorableFood[],
  parent: Partial<Record<keyof Required<KidneyNutrients>, number | null>> | null | undefined,
  servings: number,
): boolean {
  if (!parent || !isNum(servings) || servings <= 0) return false;
  const sum = sumCoreNutrients(foods);
  for (const key of Object.keys(CORE_ROUNDING) as Array<keyof Required<KidneyNutrients>>) {
    const stored = parent[key];
    if (!isNum(stored)) return false;
    const tolerance = CORE_ROUNDING[key] * (2 + 2 * servings) + 1e-9;
    if (Math.abs(sum[key] * servings - stored) > tolerance) return false;
  }
  return true;
}

export type FoodConcern = ScoreDriver | 'toxic';

export interface FoodRescore {
  kidneySafety: FoodSafety;
  riskLevel: RiskLevel;
  primaryConcern: FoodConcern | null;
  toxic: boolean;
}

/** The meal-level score fields a rescore rebuilds (as the server's Phase 2 would). */
export interface AnalysisScoreBreakdown {
  score: number;
  riskLevel: RiskLevel;
  driver: ScoreDriver;
  percentages: ScoreBreakdown['percentages'];
  dailyLimits: ScoreBreakdown['dailyLimits'];
  limitsSource: LimitSource;
}

export interface RescoreResult {
  kidneyImpactScore: number;
  overallRisk: RiskLevel;
  levels: ScoreBreakdown['levels'];
  scoreBreakdown: AnalysisScoreBreakdown;
  /** Aligned with the input foods. */
  foods: FoodRescore[];
  /** Names of the toxic foods still present (empty when the override lifted). */
  toxicFoods: string[];
  /** The context carried forward: same inputs, toxic list rebuilt from current foods. */
  scoreContext: ScoreContext;
}

/**
 * Score a food list against a stored context — exactly what the server's
 * Phase 2 computes for the same foods. Null when identity is unusable.
 */
export function rescoreFoods(foods: readonly RescoreFood[], ctx: ScoreContext): RescoreResult | null {
  if (!hasUsableFoodKeys(foods)) return null;
  const r = resolvedFromScoreContext(ctx);
  const breakdown = scoreMealTotals(sumCoreNutrients(foods), r);
  const toxicKeys = new Set(ctx.toxic.map((x) => x.foodKey));
  const toxicFlags = foods.map((f) => toxicKeys.has(f.key as string) || isToxicFoodName(f.name));
  const anyToxic = toxicFlags.some(Boolean);
  const scored: FoodRescore[] = foods.map((f, i) => {
    if (toxicFlags[i]) return { kidneySafety: 'limit', riskLevel: 'high', primaryConcern: 'toxic', toxic: true };
    const fb = scoreFood(f, r);
    return { ...fb, toxic: false };
  });
  const score = anyToxic ? 100 : breakdown.score;
  const riskLevel: RiskLevel = anyToxic ? 'high' : breakdown.riskLevel;
  return {
    kidneyImpactScore: score,
    overallRisk: riskLevel,
    levels: breakdown.levels,
    scoreBreakdown: {
      score,
      riskLevel,
      driver: breakdown.driver,
      percentages: breakdown.percentages,
      dailyLimits: breakdown.dailyLimits,
      limitsSource: r.limitsSource,
    },
    foods: scored,
    toxicFoods: foods.filter((_, i) => toxicFlags[i]).map((f) => f.name || ''),
    scoreContext: {
      ...ctx,
      toxic: foods
        .filter((_, i) => toxicFlags[i])
        .map((f) => ({ foodKey: f.key as string, name: f.name || '' })),
    },
  };
}

/** Why a stored context was not accepted for replay. */
export type ScoreContextRejection = 'none' | 'version' | 'keys' | 'basis' | 'replay';

export type ScoreContextAcceptance =
  | { ok: true; context: ScoreContext }
  | { ok: false; reason: ScoreContextRejection };

/**
 * Accept a stored context for replay only when everything still lines up:
 * known version + basis, unique non-empty keys on the stored foods, foods that
 * add up to the stored parent totals, and a no-edit replay that reproduces the
 * stored score (so an older client that changed the foods without re-scoring
 * is caught too).
 */
export function acceptScoreContext(input: {
  scoreContext: unknown;
  storedScore: number | null | undefined;
  foods: readonly RescoreFood[];
  parent: Partial<Record<keyof Required<KidneyNutrients>, number | null>> | null | undefined;
  servings: number;
}): ScoreContextAcceptance {
  if (input.scoreContext == null) return { ok: false, reason: 'none' };
  const context = parseScoreContext(input.scoreContext);
  if (!context) return { ok: false, reason: 'version' };
  if (!hasUsableFoodKeys(input.foods)) return { ok: false, reason: 'keys' };
  if (!basisMatches(input.foods, input.parent, input.servings)) return { ok: false, reason: 'basis' };
  const replay = rescoreFoods(input.foods, context);
  if (!replay || (input.storedScore != null && replay.kidneyImpactScore !== input.storedScore)) {
    return { ok: false, reason: 'replay' };
  }
  return { ok: true, context };
}

/** What a food edit means for the score. Servings are never part of it (per serving). */
function scoringSignature(foods: readonly RescoreFood[]): string {
  return JSON.stringify(
    foods.map((f) => [
      f.key ?? null,
      f.name ?? '',
      f.sodiumMg || 0,
      f.potassiumMg || 0,
      f.phosphorusMg || 0,
      f.proteinG ?? null,
    ]),
  );
}

export type FoodEditScoring =
  /** Nothing scoring-relevant changed: keep the stored analysis as it is. */
  | { kind: 'unchanged'; acceptance: ScoreContextAcceptance }
  /** Re-scored from the stored context. */
  | { kind: 'rescored'; result: RescoreResult }
  /** Legacy / unusable context: keep the stored score, mark it stale. */
  | { kind: 'stale'; reason: ScoreContextRejection };

/**
 * The one decision every editor makes after a food edit: compare the edited
 * foods with the stored ones, and re-score only through an accepted context.
 */
export function scoreFoodEdit(input: {
  scoreContext: unknown;
  storedScore: number | null | undefined;
  originalFoods: readonly RescoreFood[];
  editedFoods: readonly RescoreFood[];
  parent: Partial<Record<keyof Required<KidneyNutrients>, number | null>> | null | undefined;
  servings: number;
}): FoodEditScoring {
  const acceptance = acceptScoreContext({
    scoreContext: input.scoreContext,
    storedScore: input.storedScore,
    foods: input.originalFoods,
    parent: input.parent,
    servings: input.servings,
  });
  if (scoringSignature(input.originalFoods) === scoringSignature(input.editedFoods)) {
    return { kind: 'unchanged', acceptance };
  }
  if (!acceptance.ok) return { kind: 'stale', reason: acceptance.reason };
  const result = input.editedFoods.length ? rescoreFoods(input.editedFoods, acceptance.context) : null;
  if (!result) return { kind: 'stale', reason: 'keys' };
  return { kind: 'rescored', result };
}
